import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MessageSquare, AlertCircle, User, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getMarketplaceItemById, removeFromMarketplace } from "@/services/marketplaceService";
import { MarketplaceItem, UserRank } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { ContactSeller } from "@/components/messages/ContactSeller";
import { BanknoteCatalogDetailMinimized } from "@/components/BanknoteCatalogDetailMinimized";
import { BanknoteProvider } from "@/context/BanknoteContext";
import ImagePreview from "@/components/shared/ImagePreview";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/context/LanguageContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  formatAuctionDateTime,
  formatListingPrice,
  formatReferenceCode,
  getListingHostname,
  isListingEnded,
} from "@/lib/marketplaceListing";
import { archiveListing, setListingSold, setRealizedPrice } from "@/services/marketplaceService";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { MarketplaceListingDialog } from "@/components/marketplace/MarketplaceListingDialog";

const MarketplaceItemDetail = () => {
  console.log('Rendering MarketplaceItemDetail component');
  const { id } = useParams<{ id: string }>();
  console.log('MarketplaceItemDetail ID from params:', id);

  const [item, setItem] = useState<MarketplaceItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageOrientations, setImageOrientations] = useState<Record<number, 'vertical' | 'horizontal'>>({});
  const [isRemoving, setIsRemoving] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [realizedInput, setRealizedInput] = useState('');
  const [isListingDialogOpen, setIsListingDialogOpen] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { direction, currentLanguage } = useLanguage();
  const { t } = useTranslation(['marketplace']);

  // Memoize the fallback function to prevent infinite re-renders
  const tWithFallback = useMemo(() => {
    return (key: string, fallback: string) => {
      const translation = t(key);
      return translation === key ? fallback : translation;
    };
  }, [t]);

  // Image orientation detection function
  const getImageOrientation = (imageUrl: string): Promise<'vertical' | 'horizontal'> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(img.height > img.width ? 'vertical' : 'horizontal');
      };
      img.onerror = () => {
        resolve('horizontal'); // Default to horizontal if image fails to load
      };
      img.src = imageUrl;
    });
  };

  // Determine image orientations when images change
  useEffect(() => {
    const determineOrientations = async () => {
      if (!item?.collectionItem) return;

      const displayImages = [
        item.collectionItem.obverseImage,
        item.collectionItem.reverseImage
      ].filter(Boolean) as string[];

      const orientations: Record<number, 'vertical' | 'horizontal'> = {};

      for (let i = 0; i < displayImages.length; i++) {
        orientations[i] = await getImageOrientation(displayImages[i]);
      }

      setImageOrientations(orientations);
    };

    if (item?.collectionItem) {
      determineOrientations();
    }
  }, [item?.collectionItem]);

  const fetchItem = async () => {
    if (!id) {
      console.log('No ID provided in params');
      return;
    }

    console.log('Starting to fetch marketplace item with ID:', id);
    setLoading(true);
    try {
      // We're getting the marketplace item by its ID directly
      const fetchedItem = await getMarketplaceItemById(id);
      console.log('Fetched marketplace item result:', fetchedItem);

      if (!fetchedItem) {
        console.error('Item not found or no longer available');
        setError(tWithFallback('status.itemNotFound', 'Item not found or no longer available'));
        return;
      }

      console.log('Setting marketplace item in state:', fetchedItem);
      setItem(fetchedItem);

    } catch (err) {
      console.error("Error fetching marketplace item:", err);
      setError(tWithFallback('status.failedToLoadItem', 'Failed to load marketplace item'));
      toast({
        title: tWithFallback('status.error', 'Error'),
        description: tWithFallback('status.couldNotLoadDetails', 'Could not load the marketplace item details'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      console.log('Finished loading marketplace item');
    }
  };

  useEffect(() => {
    fetchItem();
  }, [id, toast]);




  // Helper function to get localized field values
  const getLocalizedField = (field: string, fieldType: 'face_value' | 'country'): string => {
    if (currentLanguage === 'en' || !field) {
      return field || '';
    }

    const banknoteAny = banknote as any;
    let languageSpecificField: string | undefined;

    if (currentLanguage === 'ar') {
      languageSpecificField = banknoteAny?.[`${fieldType}_ar`];
    } else if (currentLanguage === 'tr') {
      languageSpecificField = banknoteAny?.[`${fieldType}_tr`];
    }

    return languageSpecificField || field;
  };




  console.log('Current item state:', item);
  console.log('Loading state:', loading);
  console.log('Error state:', error);

  if (loading) {
    return (
      <div className="container py-8">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ottoman-600"></div>
        </div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="container py-8">
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{tWithFallback('status.error', 'Error')}</AlertTitle>
          <AlertDescription>
            {error || tWithFallback('status.itemNotFound', 'Item not found or no longer available')}
          </AlertDescription>
        </Alert>

        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {tWithFallback('actions.backToMarketplace', 'Back to Marketplace')}
        </Button>
      </div>
    );
  }

  console.log('Rendering marketplace item details with data:', item);
  const { collectionItem, seller, status } = item;
  const { banknote, condition, salePrice, publicNote, privateNote, obverseImage, reverseImage } = collectionItem;

  const isAuction = item.listing_type === 'auction';
  const isOwner = Boolean(user?.id && seller && user.id === seller.id);
  // Show the stored Price Realized in the input so it can be corrected, not just added.
  const realizedValue = realizedInput || (item.realized_price != null ? String(item.realized_price) : '');
  const auctionEnded = isAuction && isListingEnded(item);
  const showSource = Boolean(item.external_listing_url && item.is_url_approved);

  const handleToggleSold = async (checked: boolean) => {
    const ok = await setListingSold(item.id, checked);
    if (ok) fetchItem();
    else toast({ title: t('listing.saveError'), variant: 'destructive' });
  };

  const handleUpdateRealized = async () => {
    const price = parseFloat(realizedValue);
    if (Number.isNaN(price)) return;
    const ok = await setRealizedPrice(item.id, price);
    if (ok) {
      fetchItem();
    } else {
      toast({ title: t('listing.saveError'), variant: 'destructive' });
    }
  };

  const handleArchive = async () => {
    setIsArchiving(true);
    const ok = await archiveListing(item.id);
    setIsArchiving(false);
    if (ok) {
      toast({ title: t('listing.archiveSuccess') });
      fetchItem();
    } else {
      toast({ title: t('listing.saveError'), variant: 'destructive' });
    }
  };

  console.log('Banknote data for detail view:', banknote);
  console.log('Image sources:', { obverseImage, reverseImage, banknoteImages: banknote.imageUrls });

  const sellerRank = seller?.rank || "Newbie";

  // Only the seller's own photos of THIS copy. Falling back to the catalog
  // images would show a pristine reference scan for a note whose real
  // condition is unknown — misleading in a sales context, and inconsistent
  // with the card and the unlisted detail page, which both show a placeholder.
  // The catalog images remain visible in the Banknote Details section below.
  const displayImages = [obverseImage, reverseImage].filter(Boolean) as string[];

  const openImageViewer = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };


  const handleMessageClick = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    navigate(`/messaging/${seller.id}`);
  };

  const handleRemoveFromMarketplace = async () => {
    if (!item || !user) return;

    console.log('Starting removeFromMarketplace with:', {
      collectionItemId: item.collectionItem.id,
      marketplaceItemId: item.id
    });

    setIsRemoving(true);
    try {
      const success = await removeFromMarketplace(item.collectionItem.id, item.id);
      console.log('removeFromMarketplace result:', success);
      
      if (success) {
        toast({
          title: tWithFallback('actions.removedFromMarketplace', 'Removed from Marketplace'),
          description: tWithFallback('actions.itemRemovedFromMarketplaceDescription', 'Your item has been removed from the marketplace and is no longer for sale.'),
        });
        // Navigate back to collection or marketplace
        navigate('/marketplace');
      } else {
        throw new Error('Failed to remove item from marketplace');
      }
    } catch (error) {
      console.error('Error removing item from marketplace:', error);
      toast({
        title: tWithFallback('status.error', 'Error'),
        description: tWithFallback('actions.failedToRemoveFromMarketplace', 'Failed to remove item from marketplace. Please try again.'),
        variant: 'destructive',
      });
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="container py-8">
      <div className="flex items-center gap-2 mb-1">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          {direction === 'rtl' ? <ArrowRight className="h-4 w-4 mr-2" /> : <ArrowLeft className="h-4 w-4 mr-2" />}
          {tWithFallback('actions.back', 'Back')}
        </Button>
      </div>

      {/* Status indicator */}
      {/* <div className="mb-6">
        <Badge variant={status === "Available" ? "primary" : status === "Reserved" ? "secondary" : "destructive"}>
          {status}
        </Badge>
      </div> */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left column: Images */}
        <div>
          <Card className="mb-6">
            <CardContent className="px-2 pt-2 pb-2">
              <div className="flex flex-col space-y-3">
                {displayImages.length > 0 ? (
                  <div>
                    {displayImages.length === 2 ? (
                      // For exactly 2 images (obverse/reverse), check orientations
                      (() => {
                        const firstOrientation = imageOrientations[0];
                        const secondOrientation = imageOrientations[1];

                        // If both images are vertical, display side by side
                        if (firstOrientation === 'vertical' && secondOrientation === 'vertical') {
                          return (
                            <div className="grid grid-cols-2 gap-3">
                              {displayImages.map((url, index) => (
                                <div key={index} className="relative">
                                  <div
                                    className="w-full cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => openImageViewer(url)}
                                  >
                                    <div className="w-full overflow-hidden border">
                                      <img
                                      alt={` ${collectionItem.banknote?.denomination} banknote from ${collectionItem.banknote?.country}, issued in ${collectionItem.banknote?.year}`}
                                        src={url}
                                        className="w-full h-auto object-contain"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        }

                        // If any image is horizontal, stack them vertically
                        return (
                          <div className="flex flex-col space-y-3">
                            {displayImages.map((url, index) => (
                              <div key={index} className="relative">
                                <div
                                  className="w-full cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => openImageViewer(url)}
                                >
                                  <div className="w-full overflow-hidden border">
                                    <img
                                      alt={` ${collectionItem.banknote?.denomination} banknote from ${collectionItem.banknote?.country}, issued in ${collectionItem.banknote?.year}`}
                                      src={url}
                                      className="w-full h-auto object-contain"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()
                    ) : (
                      // For more than 2 images, stack them vertically
                      displayImages.map((url, index) => (
                        <div key={index} className="w-full relative">
                          <div
                            className="w-full cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => openImageViewer(url)}
                          >
                            <div className="w-full overflow-hidden border">
                              <img
                                alt={` ${collectionItem.banknote?.denomination} banknote from ${collectionItem.banknote?.country}, issued in ${collectionItem.banknote?.year}`}
                                src={url}
                                className="w-full h-auto object-contain"
                              />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="p-6 text-center bg-muted rounded-md">
                    <p className="text-muted-foreground">{tWithFallback('item.noImagesAvailable', 'No images available')}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>


        </div>

        {/* Right column: Details */}
        <div>
          <Card className="mb-6">
            <CardContent className="p-6">

              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <h2 className={`text-2xl font-serif font-bold text-parchment-500 ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
                    <span> {getLocalizedField(banknote.denomination, 'face_value')} </span>
                  </h2>
                  {banknote.extendedPickNumber && (
                    <span className="text-xl font-bold text-black-400">
                      ({banknote.newExtendedPickNumber || banknote.extendedPickNumber})
                    </span>
                  )}
                </div>

                <div className="text-3xl font-bold text-ottoman-500">
                  {!isAuction && salePrice != null && (
                    <span className={`${direction === 'rtl' ? 'text-left' : 'text-right'}`}> {formatListingPrice(salePrice, item.currency)} </span>
                  )}
                </div>
              </div>

              {/* Country/year sits directly under the title, same order as the card. */}
              <div className="mb-3">
                <p className="text-lg text-ottoman-300">
                  {getLocalizedField(banknote.country, 'country')}
                  {banknote.country && banknote.year ? ', ' : ''}
                  {banknote.year}
                </p>
                <div className={`mt-2 ${direction === "rtl" ? "text-right" : "text-left"}`}>
                  {collectionItem.condition && !collectionItem.grade && (
                    <Badge variant="secondary">
                      {collectionItem.condition}
                    </Badge>
                  )}
                  {collectionItem.grade && (
                    <Badge variant="secondary">
                      {collectionItem.grade_by && `${collectionItem.grade_by} `}
                      {collectionItem.grade}
                      {collectionItem.grade_condition_description &&
                        ` - ${collectionItem.grade_condition_description}`}
                    </Badge>
                  )}
                </div>
              </div>

              <p className="text-lg font-bold text-black dark:text-white mt-1">
                <span>{isAuction ? t('listing.auctionItem') : t('listing.buyNowItem')}</span>
              </p>
              {item.reference_code && (
                <p className="text-xs text-muted-foreground">
                  {t('listing.referenceId')} {formatReferenceCode(item.reference_code)}
                </p>
              )}
              {!isAuction && item.is_sold && (
                <p className="mt-2 inline-block rounded bg-destructive/90 px-3 py-1 text-lg font-bold text-destructive-foreground">
                  {t('listing.itemSold')}
                </p>
              )}
              {isAuction && item.realized_price != null && (
                <p className="mt-2 inline-block rounded bg-black/80 px-3 py-1 text-lg font-bold text-white">
                  {t('listing.priceRealizedLabel', { price: formatListingPrice(item.realized_price, item.currency) })}
                </p>
              )}
              {item.public_remark && (
                <p className="text-sm text-muted-foreground mt-2">{item.public_remark}</p>
              )}
              {isAuction && (
                <div className="mt-3 space-y-1 text-sm">
                  {item.auction_at && (
                    <div className="rounded border bg-muted/40 px-3 py-2">
                      <p className="text-xs text-muted-foreground">{t('listing.auctionDateTime')}</p>
                      <p className="font-bold">{formatAuctionDateTime(item.auction_at, item.auction_timezone ?? null)}</p>
                    </div>
                  )}
                  {item.lot_number && <p>{t('listing.lot')}: {item.lot_number}</p>}
                  {item.start_price != null && <p>{t('listing.startPrice')}: {formatListingPrice(item.start_price, item.currency)}</p>}
                  {item.estimated_price && <p>{t('listing.estimatedPrice')}: {formatListingPrice(item.estimated_price, item.currency)}</p>}
                  {item.realized_price != null && <p className="font-bold">{t('listing.realizedPrice')}: {formatListingPrice(item.realized_price, item.currency)}</p>}
                </div>
              )}

              {/* Owner-only inline controls (spec §8.2) */}
              {isOwner && !isAuction && item.status === 'Available' && (
                <div className="mt-3 flex items-center gap-2">
                  <Checkbox
                    id="detail-mark-sold"
                    checked={Boolean(item.is_sold)}
                    onCheckedChange={(v) => handleToggleSold(v === true)}
                  />
                  <label htmlFor="detail-mark-sold" className="text-sm cursor-pointer">
                    {t('listing.markAsSold')}
                  </label>
                </div>
              )}
              {isOwner && isAuction && auctionEnded && (
                <div className="mt-3 space-y-1">
                  <p className="text-sm">{t('listing.enterRealizedPrice')}</p>
                  <div className="flex gap-2">
                    <Input
                      className="h-8 w-32"
                      value={realizedValue}
                      onChange={(e) => {
                        if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                          setRealizedInput(e.target.value);
                        }
                      }}
                    />
                    <Button size="sm" disabled={!realizedValue} onClick={handleUpdateRealized}>
                      {t('listing.update')}
                    </Button>
                  </div>
                </div>
              )}
              {showSource && (
                <div className="mt-3">
                  <Button
                    className="w-full sm:w-auto bg-ottoman-600 hover:bg-ottoman-700 text-white font-semibold"
                    onClick={() => window.open(item.external_listing_url!, '_blank', 'noopener,noreferrer')}
                  >
                    {t('listing.viewSource')}
                  </Button>
                  {getListingHostname(item.external_listing_url) && (
                    <p className="text-sm text-ottoman-700 dark:text-ottoman-300 mt-1">
                      {getListingHostname(item.external_listing_url)}
                    </p>
                  )}
                </div>
              )}


              {/* {publicNote && (
                <div className="mt-4">
                  <p className="text-sm text-ottoman-400">Seller's Note</p>
                  <p className="mt-1 text-ottoman-200 p-3 bg-ottoman-900/20 rounded-md">
                    {publicNote}
                  </p>
                </div>
              )} */}
            </CardContent>

            {/* Seller information */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {/* Seller info */}
                  <div className="flex items-center gap-2">
                    <Avatar
                      className="h-8 w-8 text-ottoman-400 cursor-pointer"
                      onClick={() => navigate(`/profile/${seller.id}`)}
                    >
                      <AvatarImage src={seller.avatarUrl} />
                      <AvatarFallback>
                        {seller.username.charAt(0)}
                      </AvatarFallback>
                    </Avatar>

                    <div>
                      <Link
                        to={`/profile/${seller.id}`}
                        className="text-ottoman-500 hover:text-ottoman-600"
                      >
                        {seller.username}{" "}
                        <Badge variant="user" rank={sellerRank as UserRank} role={seller.role} />
                      </Link>
                    </div>
                  </div>

                  {/* Buttons */}
                  {!isAuction && user && user.id !== seller.id && (
                    <div>
                      <Button variant="outline" size="sm" onClick={handleMessageClick}>
                        <MessageSquare className="h-4 w-4 mr-2" />
                        {tWithFallback("actions.message", "Message")}
                      </Button>
                    </div>
                  )}

                  {user && user.id === seller.id && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsListingDialogOpen(true)}
                      >
                        {t('listing.editItem')}
                      </Button>
                      {!item.archived_at && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleArchive}
                          disabled={isArchiving}
                        >
                          {t('listing.archive')}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRemoveFromMarketplace}
                        disabled={isRemoving}
                      >
                        {isRemoving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                            {tWithFallback("actions.removing", "Removing...")}
                          </>
                        ) : (
                          <>
                            {tWithFallback(
                              "actions.removeFromMarketplace",
                              "Remove from Marketplace"
                            )}
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

          </Card>

          {/* Wrap the BanknoteCatalogDetailMinimized component with BanknoteProvider */}
          <Card className="border-t-4 border-t-primary shadow-md">
            <CardHeader className="border-b bg-muted/20">
              <div className="flex justify-between items-center">
                <CardTitle className="text-xl m-0">
                  <span>{tWithFallback('item.banknoteDetails', 'Banknote Details')}</span>
                </CardTitle>
              </div>
              <CardDescription className={`${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
                {tWithFallback('item.detailedInformation', 'Detailed information about this banknote')}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <BanknoteCatalogDetailMinimized
                banknote={banknote}
                onImageClick={(url) => setSelectedImage(url)}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image Preview Dialog */}
      <ImagePreview
        src={selectedImage}
        onClose={() => setSelectedImage(null)}
      />

      {item.collectionItem?.id && (
        <MarketplaceListingDialog
          open={isListingDialogOpen}
          onOpenChange={setIsListingDialogOpen}
          collectionItemId={item.collectionItem.id}
          onSaved={() => fetchItem()}
        />
      )}
    </div>
  );
};

export default MarketplaceItemDetail;
